import { useEffect, useId, useRef, type ReactNode } from "react";
import { css } from "@emotion/css";
import {
  alpha,
  Box,
  ClickAwayListener,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { ThalamosLink } from "../ThalamosLink.js";
import { getIconFromName } from "../util.js";
import { Button } from "./Button/Button.js";

export type MenuOptionsType = {
  icon:
    | "view"
    | "person"
    | "delete"
    | "transfer"
    | "edit"
    | "download"
    | "upload"
    | "cancel"
    | "accept"
    | "amend"
    | string;
  name: string;
  onClick?: () => void;

  link?: string;

  disabled: boolean;
  tooltip?: string[];
};

interface RefObject<T> {
  readonly current: T | null;
}

interface MenuFlyoutProps<T> {
  options?: MenuOptionsType[];
  emptyMenuMessage?: string;
  isOpen?: boolean;
  cardRef?: RefObject<T>;
  onClose: () => void;
  customItems?: ReactNode;
  badgeColour?: "error" | "warning";
}

const defaultProps: Partial<MenuFlyoutProps<any>> = {
  emptyMenuMessage: undefined,
  isOpen: undefined,
  cardRef: undefined,
  badgeColour: undefined,
};

// Tracks the single flyout that is currently open across the whole app so that
// opening one closes any other. Each consumer keeps its own local open state, so
// without this coordination the popouts stack on top of one another.
let currentOpen: { id: string; close: () => void } | null = null;

export const MenuFlyout = (props: MenuFlyoutProps<any>) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const allProps: MenuFlyoutProps<any> = { ...defaultProps, ...props };
  const {
    options,
    emptyMenuMessage,
    isOpen,
    cardRef,
    onClose,
    customItems,
    badgeColour,
  } = allProps;

  // `onClose` is a fresh closure each render, so keep it in a ref to give this
  // instance a stable close handler the coordinator can call.
  const instanceId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) {
      if (currentOpen?.id === instanceId) {
        currentOpen = null;
      }
      return;
    }

    // Opening: close whichever flyout was previously open, then register this
    // one as the single open flyout.
    if (currentOpen && currentOpen.id !== instanceId) {
      currentOpen.close();
    }
    currentOpen = {
      id: instanceId,
      close: () => {
        onCloseRef.current();
      },
    };

    // Clear the tracker if this flyout unmounts (e.g. route change) while still
    // open, so we never hold a stale close callback or call it on an unmounted
    // component.
    return () => {
      if (currentOpen?.id === instanceId) {
        currentOpen = null;
      }
    };
  }, [isOpen, instanceId]);

  const handleOptionSelection = ({
    onClick,
    disabled,
  }: {
    onClick?: MenuOptionsType["onClick"];
    disabled: MenuOptionsType["disabled"];
  }) => {
    if (disabled) return;

    onClose();
    if (onClick) onClick();
  };

  const styles = {
    container: css`
      width: ${theme.spacing(30)};
    `,
    icon: css`
      padding: 0;
      color: ${theme.palette.text.secondary};
      margin-right: ${theme.spacing(1.5)};
      &:hover {
        color: ${theme.palette.primary.main};
      }
    `,
    listItem: css`
      height: ${theme.spacing(7)};
      &:hover {
        background-color: ${theme.palette.background.default};
        cursor: pointer;
        & :first-of-type {
          color: ${theme.palette.primary.main};
          fill: ${theme.palette.primary.main};
        }
      }
    `,
  };

  const renderFlyoutOptions = () => {
    if (options?.length || customItems) {
      return (
        <List>
          {options?.map((op) => (
            <ThalamosLink to={op.link} key={op.name}>
              <Tooltip
                placement="left"
                arrow
                title={
                  op.tooltip && (
                    <Stack>
                      {op.tooltip.map((t, index) => (
                        <Typography key={`${t}-${index}`}>{t}</Typography>
                      ))}
                    </Stack>
                  )
                }
              >
                <ListItemButton
                  key={op.name}
                  disabled={op.disabled}
                  classes={{ root: styles.listItem }}
                  onClick={() => handleOptionSelection(op)}
                >
                  <ListItemIcon
                    sx={{ minWidth: (theme) => theme.spacing(1.5) }}
                    classes={{ root: styles.icon }}
                  >
                    {getIconFromName(op.icon)}
                  </ListItemIcon>
                  <Typography
                    css={css`
                      font-size: ${theme.spacing(2)};
                    `}
                    variant="inherit"
                  >
                    {op.name}
                  </Typography>
                </ListItemButton>
              </Tooltip>
            </ThalamosLink>
          ))}
          {options?.length && customItems ? <Divider component="li" /> : null}
          {customItems}
        </List>
      );
    }
    return (
      <List
        css={css`
          background-color: ${theme.palette.background.default};
        `}
      >
        <ListItem key="emptyMessage">
          <Typography
            css={css`
              font-size: ${theme.spacing(1.75)};
            `}
            variant="inherit"
          >
            {emptyMenuMessage}
          </Typography>
        </ListItem>
      </List>
    );
  };

  const actionButton = (
    <Button
      variant="outlined"
      sx={{
        display: "flex",
        backgroundColor: isOpen
          ? alpha(theme.palette.primary.main, 0.1)
          : theme.palette.common.white,
      }}
      label={t("buttonLabels.action")}
      color={"primary"}
      customFontSize={1.8}
      {...(badgeColour !== undefined && {
        customEndIcon: (
          <Box
            component="span"
            data-testid={`sync-badge-${badgeColour}`}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: `${badgeColour}.main`,
              display: "inline-block",
              flexShrink: 0,
            }}
          />
        ),
      })}
    />
  );

  return (
    <Box>
      {actionButton}

      <Popper
        open={!!isOpen}
        anchorEl={() => cardRef?.current}
        placement="bottom-end"
        css={styles.container}
        sx={{ zIndex: 9999 }}
      >
        <Paper>
          <ClickAwayListener onClickAway={onClose}>
            <Box>{renderFlyoutOptions()}</Box>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </Box>
  );
};
